import Box from '@mui/material/Box';
import { alpha, keyframes, styled } from '@mui/material/styles';
import type { SkeletonAnimation, SkeletonVariant } from './Skeleton.types';

interface SkeletonRootStyleProps {
  animation: SkeletonAnimation;
  height?: number | string;
  radius?: number | string;
  skeletonVariant: SkeletonVariant;
  width?: number | string;
}

const shouldForwardSkeletonProp = (prop: PropertyKey) =>
  prop !== 'animation'
  && prop !== 'height'
  && prop !== 'radius'
  && prop !== 'skeletonVariant'
  && prop !== 'width';

const skeletonPulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
  100% {
    opacity: 1;
  }
`;

const skeletonWave = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const getSkeletonRadius = ({
  radius,
  skeletonVariant,
}: Pick<SkeletonRootStyleProps, 'radius' | 'skeletonVariant'>) => {
  if (radius !== undefined) {
    return radius;
  }

  if (skeletonVariant === 'circle' || skeletonVariant === 'pill') {
    return 999;
  }

  if (skeletonVariant === 'text') {
    return 4;
  }

  return 8;
};

const getSkeletonSize = ({
  height,
  skeletonVariant,
  width,
}: Pick<SkeletonRootStyleProps, 'height' | 'skeletonVariant' | 'width'>) => {
  if (skeletonVariant === 'circle') {
    const size = width ?? height ?? 32;

    return {
      height: size,
      width: size,
    };
  }

  if (skeletonVariant === 'text') {
    return {
      height: height ?? 14,
      width: width ?? '100%',
    };
  }

  return {
    height: height ?? 24,
    width: width ?? '100%',
  };
};

export const SkeletonRoot = styled(Box, {
  shouldForwardProp: shouldForwardSkeletonProp,
})<SkeletonRootStyleProps>(({ animation, height, radius, skeletonVariant, theme, width }) => ({
  ...getSkeletonSize({ height, skeletonVariant, width }),
  backgroundColor: theme.palette.action.disabledBackground,
  borderRadius: getSkeletonRadius({ radius, skeletonVariant }),
  display: 'block',
  flexShrink: 0,
  overflow: 'hidden',
  position: 'relative',
  ...(animation === 'pulse' && {
    animation: `${skeletonPulse} 1.6s ease-in-out 0.5s infinite`,
  }),
  ...(animation === 'wave' && {
    '&::after': {
      animation: `${skeletonWave} 1.6s linear 0.5s infinite`,
      background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.7)}, transparent)`,
      content: '""',
      inset: 0,
      position: 'absolute',
      transform: 'translateX(-100%)',
    },
  }),
}));
